import { useQuery } from '@tanstack/react-query';
import { getOperationDepositCommission } from '@/shared/api';
import { CACHE_TIME_5_MINUTES, DEFAULT_RETRY_COUNT } from '@/shared/config';
import type {
  CommissionResponse,
  DepositCreateOperationType,
} from '@/shared/types';

interface UseDepositCommissionOptions<
  TParams = Record<string, string | number>,
> {
  operationType: DepositCreateOperationType;
  params: TParams;
  enabled?: boolean;
  queryKey?: string[];
  staleTime?: number;
  gcTime?: number;
  retry?: number;
}

export function useDepositCommission<
  TResponse extends CommissionResponse = CommissionResponse,
  TParams extends Record<string, string | number> = Record<
    string,
    string | number
  >,
>({
  operationType,
  params,
  enabled = true,
  queryKey,
  staleTime = CACHE_TIME_5_MINUTES,
  gcTime = CACHE_TIME_5_MINUTES,
  retry = DEFAULT_RETRY_COUNT,
}: UseDepositCommissionOptions<TParams>) {
  const defaultQueryKey = [
    `${operationType}DepositCommission`,
    ...Object.values(params),
  ];
  const finalQueryKey = queryKey || defaultQueryKey;

  const query = useQuery<TResponse, Error>({
    queryKey: finalQueryKey,
    queryFn: async () => {
      return await getOperationDepositCommission<TResponse, TParams>(
        operationType,
        params
      );
    },
    enabled,
    staleTime,
    gcTime,
    retry,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    status: query.status,
    refetch: query.refetch,
    isFetching: query.isFetching,
    query,
  };
}
